import { computed } from '@angular/core';
import { type UUID } from '@bizzdesign/bizzdesign-sdk/uuid';
import { isNotUndefinedOrNull } from '@bizzdesign/bizzdesign-sdk';
import {
  OBJECT_SELECTION_LIMIT,
  type ObjectDto,
  type StatusDialogSettings,
} from '@bizzdesign/shared/models';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';

export type EditDataDialogState = {
  selectedObjects: ObjectDto[];
};

export const initialEditDataDialogState: EditDataDialogState = {
  selectedObjects: [],
};

type WarningDialogCase =
  | 'onlyExternal'
  | 'someExternalAndTooMany'
  | 'someExternal'
  | 'tooManyLocal';

type WarningDialogKeys = {
  message: string;
  title: string;
  confirmLabel: string;
  dismissLabel: string | undefined;
  details: string | undefined;
};

const WARNING_DIALOG_KEYS: Record<WarningDialogCase, WarningDialogKeys> = {
  onlyExternal: {
    message: 'editData.onlyExternalObjectsDialog.message',
    title: 'editData.onlyExternalObjectsDialog.title',
    confirmLabel: 'editData.onlyExternalObjectsDialog.refineButtonLabel',
    dismissLabel: undefined,
    details: 'editData.onlyExternalObjectsDialog.details',
  },
  someExternalAndTooMany: {
    message: 'editData.someExternalObjectsDialog.message',
    title: 'editData.tooManyObjectsDialog.title',
    confirmLabel: 'editData.tooManyObjectsDialog.editButtonLabel',
    dismissLabel: 'editData.tooManyObjectsDialog.refineButtonLabel',
    details: 'editData.tooManyObjectsDialog.warning',
  },
  someExternal: {
    message: 'editData.someExternalObjectsDialog.message',
    title: 'editData.someExternalObjectsDialog.title',
    confirmLabel: 'editData.someExternalObjectsDialog.editButtonLabel',
    dismissLabel: 'editData.someExternalObjectsDialog.cancelButtonLabel',
    details: undefined,
  },
  tooManyLocal: {
    message: 'editData.tooManyLocalObjectsDialog.message',
    title: 'editData.tooManyLocalObjectsDialog.title',
    confirmLabel: 'editData.tooManyObjectsDialog.editButtonLabel',
    dismissLabel: 'editData.tooManyObjectsDialog.refineButtonLabel',
    details: 'editData.tooManyObjectsDialog.warning',
  },
};

const resolveWarningDialogCase = (
  onlyExternal: boolean,
  someExternal: boolean,
  tooManyLocal: boolean,
): WarningDialogCase => {
  if (onlyExternal) {
    return 'onlyExternal';
  }
  if (someExternal && tooManyLocal) {
    return 'someExternalAndTooMany';
  }
  if (someExternal) {
    return 'someExternal';
  }

  return 'tooManyLocal';
};

export const EditDataDialogComponentStore = signalStore(
  withState(initialEditDataDialogState),
  withComputed(({ selectedObjects }) => {
    const editableObjectIds = computed<UUID[]>(() =>
      selectedObjects()
        .filter((object) => !isNotUndefinedOrNull(object.externalId))
        .map((object) => object.id),
    );

    const onlyExternalSelected = computed(
      () => selectedObjects().length > 0 && editableObjectIds().length === 0,
    );

    const someExternalSelected = computed(() => {
      const editableCount = editableObjectIds().length;
      return editableCount > 0 && editableCount !== selectedObjects().length;
    });

    const tooManyObjectsSelected = computed(
      () => editableObjectIds().length > OBJECT_SELECTION_LIMIT,
    );

    const dialogCase = computed<WarningDialogCase>(() =>
      resolveWarningDialogCase(
        onlyExternalSelected(),
        someExternalSelected(),
        tooManyObjectsSelected(),
      ),
    );

    const warningMessage = computed<string>(
      () => WARNING_DIALOG_KEYS[dialogCase()].message,
    );

    const warningDetails = computed<string | undefined>(
      () => WARNING_DIALOG_KEYS[dialogCase()].details,
    );

    const editableCount = computed(() => editableObjectIds().length);

    const warningDialogSettings = computed<StatusDialogSettings>(() => {
      const { title, confirmLabel, dismissLabel } =
        WARNING_DIALOG_KEYS[dialogCase()];

      const settings: StatusDialogSettings = {
        status: 'warning',
        display: true,
        header: { textKey: title, tScope: 'featureSearch' },
        confirmLabel: {
          textKey: confirmLabel,
          tScope: 'featureSearch',
          textParams: { count: editableCount().toString() },
        },
        dismissLabel: dismissLabel
          ? { textKey: dismissLabel, tScope: 'featureSearch' }
          : undefined,
      };
      return settings;
    });

    return {
      editableObjectIds,
      onlyExternalSelected,
      someExternalSelected,
      tooManyObjectsSelected,
      warningDialogSettings,
      warningMessage,
      warningDetails,
      editableCount,
    };
  }),
  withMethods((store) => ({
    updateSelectedObjects(selectedObjects: ObjectDto[]): void {
      patchState(store, { selectedObjects });
    },
  })),
);

export type EditDataDialogComponentStore = InstanceType<
  typeof EditDataDialogComponentStore
>;
